import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { Helmet } from 'react-helmet';
import { db } from '../firebase';
import PublicMarketingLayout from '../components/layout/PublicMarketingLayout';
import OliveBranch from '../components/marketing/OliveBranch';
import { recordFirstTouch } from '../services/acquisitionService';
import {
  ACQUISITION_CHANNEL_LABELS,
  type CampaignDoc,
} from '../types/acquisition';

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; campaign: CampaignDoc }
  | { status: 'not_found' }
  | { status: 'error'; message: string };

/**
 * Single dynamic landing-page route serving every marketing campaign.
 *
 * URL: `/c/:slug`. Slug is the human-readable shorthand a podcast host
 * reads aloud (`melio.health/c/rangan`). The page resolves the slug to a
 * `campaigns/{campaignId}` doc, records a first-touch in the visitor's
 * cookie, and renders the campaign-specific hero + CTA. Adding a new
 * campaign is a Firestore write — no deploy needed.
 *
 * See: docs/markdown/eng/CTO_BRIEF_ACQUISITION_TRACKING.md §4.1
 */
export default function CampaignLandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    if (!slug) {
      setState({ status: 'not_found' });
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const campaignsRef = collection(db, 'campaigns');
        const snap = await getDocs(
          query(
            campaignsRef,
            where('slug', '==', slug),
            where('active', '==', true),
            limit(1),
          ),
        );
        if (cancelled) return;
        if (snap.empty) {
          setState({ status: 'not_found' });
          return;
        }
        const doc = snap.docs[0];
        const campaign = { ...(doc.data() as CampaignDoc), campaignId: doc.id };
        setState({ status: 'ready', campaign });

        // First-touch capture is the whole point of this route. Even if
        // record-keeping silently fails, the page must render normally.
        try {
          recordFirstTouch(campaign);
        } catch (err) {
          // Surface to the console only — never block the visitor.
          console.warn('recordFirstTouch failed', err);
        }
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : 'Could not load this campaign.';
        setState({ status: 'error', message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <PublicMarketingLayout>
      <Helmet>
        <title>{getPageTitle(state)}</title>
        {state.status === 'ready' && (
          <meta name="description" content={state.campaign.landingHeroCopy.slice(0, 160)} />
        )}
        {/* Campaign landing pages are intentionally noindex: the canonical
            URLs for membership / assessments live elsewhere, and we don't
            want podcast-specific URLs ranking on their own. */}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {renderBody(state, slug)}
    </PublicMarketingLayout>
  );
}

function getPageTitle(state: LoadState): string {
  if (state.status === 'ready') {
    return `${state.campaign.displayName} — Melio`;
  }
  if (state.status === 'loading') return 'Welcome — Melio';
  return 'Page not found — Melio';
}

function renderBody(state: LoadState, slug: string | undefined) {
  if (state.status === 'loading') {
    return <LoadingShell />;
  }
  if (state.status === 'not_found') {
    return <NotFoundShell slug={slug} />;
  }
  if (state.status === 'error') {
    return <ErrorShell message={state.message} />;
  }
  return <CampaignHero campaign={state.campaign} />;
}

// ---------------------------------------------------------------------------
// View shells
// ---------------------------------------------------------------------------

interface CampaignHeroProps {
  campaign: CampaignDoc;
}

function CampaignHero({ campaign }: CampaignHeroProps) {
  const ctaHref = useMemo(() => {
    const params = new URLSearchParams();
    params.set('campaign', campaign.campaignId);
    if (campaign.promoCode) params.set('promo', campaign.promoCode);
    return `/enrollment?${params.toString()}`;
  }, [campaign]);

  const eyebrow = campaign.hostOrPartnerName
    ? `${ACQUISITION_CHANNEL_LABELS[campaign.channel]} · ${campaign.hostOrPartnerName}`
    : ACQUISITION_CHANNEL_LABELS[campaign.channel];

  return (
    <section
      className="px-6 md:px-10"
      style={{ backgroundColor: 'var(--gh-bone)', paddingTop: '5rem', paddingBottom: '6rem' }}
    >
      <div className="mx-auto max-w-3xl text-center">
        <div className="mb-8 flex justify-center">
          <OliveBranch width={88} color="var(--gh-oxblood)" />
        </div>

        <p
          className="gh-eyebrow-ash mb-6"
          style={{ letterSpacing: '0.22em' }}
        >
          {eyebrow}
        </p>

        <h1
          className="gh-serif"
          style={{
            color: 'var(--gh-graphite)',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.15,
            marginBottom: '1.75rem',
          }}
        >
          {campaign.landingHeroCopy}
        </h1>

        <p
          className="mx-auto max-w-xl"
          style={{
            color: 'var(--gh-graphite)',
            fontSize: '1.0625rem',
            lineHeight: 1.6,
            marginBottom: '2.5rem',
            opacity: 0.85,
          }}
        >
          A small clinical practice for people who want their genetics, family
          history, and biomarkers held together in one place — and reviewed each
          quarter by a clinician who knows them.
        </p>

        <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <Link
            to={ctaHref}
            className="gh-btn-primary"
            style={{ padding: '14px 28px', fontSize: '0.8125rem' }}
          >
            {campaign.landingCtaLabel}
          </Link>
          <Link
            to="/membership"
            className="gh-nav-link"
            style={{ fontSize: '0.875rem' }}
          >
            Read about the practice
          </Link>
        </div>

        {campaign.promoCode && (
          <p
            className="mt-10 text-xs"
            style={{ color: 'var(--gh-ash)', letterSpacing: '0.18em', textTransform: 'uppercase' }}
          >
            Code applied at checkout · {campaign.promoCode}
          </p>
        )}
      </div>
    </section>
  );
}

function LoadingShell() {
  return (
    <section
      className="flex items-center justify-center"
      style={{ backgroundColor: 'var(--gh-bone)', minHeight: '60vh' }}
    >
      <p className="gh-eyebrow-ash">Loading…</p>
    </section>
  );
}

function NotFoundShell({ slug }: { slug: string | undefined }) {
  return (
    <section
      className="flex flex-col items-center justify-center px-6 text-center"
      style={{ backgroundColor: 'var(--gh-bone)', minHeight: '60vh', paddingTop: '4rem', paddingBottom: '4rem' }}
    >
      <OliveBranch width={64} color="var(--gh-oxblood)" />
      <h1
        className="gh-serif mt-8"
        style={{ color: 'var(--gh-graphite)', fontSize: '1.75rem', marginBottom: '1rem' }}
      >
        We could not find that campaign.
      </h1>
      <p style={{ color: 'var(--gh-ash)', maxWidth: '32rem', marginBottom: '2rem' }}>
        {slug
          ? `The link "${slug}" did not match an active Melio campaign. It may have closed, or the URL may be slightly different.`
          : 'No campaign was specified.'}
      </p>
      <Link
        to="/"
        className="gh-btn-primary"
        style={{ padding: '12px 24px', fontSize: '0.8125rem' }}
      >
        Visit the homepage
      </Link>
    </section>
  );
}

function ErrorShell({ message }: { message: string }) {
  return (
    <section
      className="flex flex-col items-center justify-center px-6 text-center"
      style={{ backgroundColor: 'var(--gh-bone)', minHeight: '60vh', paddingTop: '4rem', paddingBottom: '4rem' }}
    >
      <h1
        className="gh-serif"
        style={{ color: 'var(--gh-graphite)', fontSize: '1.75rem', marginBottom: '1rem' }}
      >
        Something went wrong.
      </h1>
      <p style={{ color: 'var(--gh-ash)', maxWidth: '32rem', marginBottom: '2rem' }}>{message}</p>
      <Link to="/" className="gh-btn-primary" style={{ padding: '12px 24px', fontSize: '0.8125rem' }}>
        Visit the homepage
      </Link>
    </section>
  );
}
